import { useEffect } from "react";

const BASE_URL = "https://soloway.lk";
const DEFAULT_IMAGE = `${BASE_URL}/logo.png`;
const SITE_NAME = "Soloway Sri Lanka";

/**
 * Universal Dynamic SEO Head & Social Metadata Component for Soloway
 * Uses native DOM manipulation for 100% React 18/19 compatibility without third-party bundle errors.
 */
export default function SEOHead({
  title = "Soloway | Authentic Sri Lanka Tours, Stays & Guides",
  description = "Discover Sri Lanka authentically with Soloway. Connect with certified local tour guides, book trusted hotel stays, arrange visa assistance, and explore island tours seamlessly.",
  keywords = "sri lanka hotel, sri lanka guides, most famous places sri lanka, travel sri lanka, ella hotels, sigiriya guides, colombo accommodation, yala safari",
  canonicalPath = "",
  canonicalUrl = null,
  ogType = "website",
  ogImage = DEFAULT_IMAGE,
  noIndex = false,
  publishedTime,
  modifiedTime,
  schemaJson = null,
  structuredData = null,
}) {
  const finalSchema = structuredData || schemaJson;

  // Format Title: Max 60 chars
  let formattedTitle = title;
  if (!title.toLowerCase().includes("soloway")) {
    formattedTitle = `${title.substring(0, 48)} | Soloway`;
  }
  if (formattedTitle.length > 60) {
    formattedTitle = formattedTitle.substring(0, 57) + "...";
  }

  // Format Description: Optimize for 150-160 characters
  const formattedDescription = description.length > 160
    ? description.substring(0, 157) + "..."
    : description;

  // Determine canonical URL
  let fullCanonical = canonicalUrl;
  if (!fullCanonical) {
    fullCanonical = `${BASE_URL}${
      canonicalPath.startsWith("/") ? canonicalPath : `/${canonicalPath}`
    }`;
  }
  if (!fullCanonical.startsWith("http")) {
    fullCanonical = `${BASE_URL}${fullCanonical.startsWith("/") ? fullCanonical : `/${fullCanonical}`}`;
  }

  const absoluteOgImage = ogImage.startsWith("http") ? ogImage : `${BASE_URL}${ogImage.startsWith("/") ? ogImage : `/${ogImage}`}`;

  useEffect(() => {
    // 1. Document Title & HTML Language
    document.title = formattedTitle;
    document.documentElement.lang = "en";

    // Helper function for meta tags
    const setMetaTag = (attrName, attrValue, content) => {
      if (content === undefined || content === null) return;
      let element = document.querySelector(`meta[${attrName}="${attrValue}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attrName, attrValue);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    // Helper function for link tags
    const setLinkTag = (rel, href) => {
      if (!href) return;
      let element = document.querySelector(`link[rel="${rel}"]`);
      if (!element) {
        element = document.createElement("link");
        element.setAttribute("rel", rel);
        document.head.appendChild(element);
      }
      element.setAttribute("href", href);
    };

    // 2. Primary Meta
    setMetaTag("name", "description", formattedDescription);
    setMetaTag("name", "keywords", keywords);
    setMetaTag(
      "name",
      "robots",
      noIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
    );
    setMetaTag("name", "author", "SoloWay");
    setMetaTag("name", "publisher", "SOLOWAY.LK (PVT) LTD");

    // 3. Local Sri Lankan Geo Meta
    setMetaTag("name", "geo.region", "LK");
    setMetaTag("name", "geo.placename", "Sri Lanka");
    setMetaTag("name", "geo.position", "7.8731;80.7718");
    setMetaTag("name", "ICBM", "7.8731, 80.7718");

    // 4. Canonical Link
    setLinkTag("canonical", fullCanonical);

    // 5. OpenGraph Tags
    setMetaTag("property", "og:site_name", SITE_NAME);
    setMetaTag("property", "og:title", formattedTitle);
    setMetaTag("property", "og:description", formattedDescription);
    setMetaTag("property", "og:type", ogType);
    setMetaTag("property", "og:url", fullCanonical);
    setMetaTag("property", "og:image", absoluteOgImage);
    setMetaTag("property", "og:image:secure_url", absoluteOgImage);
    setMetaTag("property", "og:image:width", "1200");
    setMetaTag("property", "og:image:height", "630");
    setMetaTag("property", "og:locale", "en_US");

    // Clean up any legacy or fake alternate locale tags
    document.querySelectorAll('meta[property="og:locale:alternate"]').forEach((el) => el.remove());

    if (publishedTime) setMetaTag("property", "article:published_time", publishedTime);
    if (modifiedTime) setMetaTag("property", "article:modified_time", modifiedTime);

    // 6. Twitter Card Tags
    setMetaTag("name", "twitter:card", "summary_large_image");
    setMetaTag("name", "twitter:site", "@solowaylk");
    setMetaTag("name", "twitter:creator", "@solowaylk");
    setMetaTag("name", "twitter:title", formattedTitle);
    setMetaTag("name", "twitter:description", formattedDescription);
    setMetaTag("name", "twitter:image", absoluteOgImage);

    // 7. Dynamic JSON-LD Structured Data
    const schemaScriptId = "soloway-dynamic-schema-jsonld";
    let schemaScript = document.getElementById(schemaScriptId);
    if (finalSchema) {
      if (!schemaScript) {
        schemaScript = document.createElement("script");
        schemaScript.id = schemaScriptId;
        schemaScript.type = "application/ld+json";
        document.head.appendChild(schemaScript);
      }
      schemaScript.text = JSON.stringify(finalSchema);
    } else if (schemaScript) {
      schemaScript.remove();
    }
  }, [
    formattedTitle,
    formattedDescription,
    keywords,
    fullCanonical,
    ogType,
    absoluteOgImage,
    noIndex,
    publishedTime,
    modifiedTime,
    finalSchema,
  ]);

  return null;
}
